import React, { useContext, useEffect, useReducer, useState } from "react";
import DeleteAssistantConfirmDialog from "../../components/AssistantModal/DeleteAssistantConfirmDialog";

import {
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import api from "../../services/api";
import { DeleteOutline, Edit } from "@material-ui/icons";
import AssistantModal from "../../components/AssistantModal";
import ApiKeySelectorModal from "../../components/ApiKeySelectorModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { SocketContext } from "../../context/Socket/SocketContext";



const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_ASSISTANTS":
      const newItems = [];
      action.payload.forEach((item) => {
        const index = state.findIndex((a) => a.idassistant === item.idassistant);
        if (index !== -1) state[index] = item;
        else newItems.push(item);
      });
      return [...state, ...newItems];
    case "UPDATE_ASSISTANTS":
      const updatedItem = action.payload;
      const index = state.findIndex((a) => a.idassistant === updatedItem.idassistant);
      if (index !== -1) {
        state[index] = updatedItem;
        return [...state];
      }
      return [updatedItem, ...state];
    case "DELETE_ASSISTANT":
      return state.filter((a) => a.idassistant !== action.payload);
    case "RESET":
      return [];
    default:
      return state;
  }
};

const Assistants = () => {
  const classes = useStyles();
  const [assistants, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [assistantModalOpen, setAssistantModalOpen] = useState(false);
  const [keyModalOpen, setKeyModalOpen] = useState(false);
  const [loadingSync, setLoadingSync] = useState(false);
  const { user } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();
  const history = useHistory();
  const companyId = user.companyId;
  const socketManager = useContext(SocketContext);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  

  useEffect(() => {
    async function fetchData() {
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useOpenAi) {
        toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
        setTimeout(() => history.push("/"), 1000);
      }
    }
    fetchData();
  }, [getPlanCompany, companyId, history]);

  useEffect(() => {
    let isMounted = true;
  
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/assistants");
        if (isMounted) {
          dispatch({ type: "LOAD_ASSISTANTS", payload: data });
        }
      } catch (err) {
        if (isMounted) toastError(err);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
  
    return () => { isMounted = false; };
  }, []);
  

  useEffect(() => {
    const socket = socketManager.getSocket(companyId);
    socket.on(`company-${companyId}-assistant`, (data) => {
      if (["update", "create"].includes(data.action)) {
        dispatch({ type: "UPDATE_ASSISTANTS", payload: data.assistant });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_ASSISTANT", payload: data.assistantId });
      }
    });
    return () => socket.disconnect();
  }, [companyId, socketManager]);

  const getAssistants = async () => {
    const { data } = await api.get("/assistants");
    dispatch({ type: "LOAD_ASSISTANTS", payload: data });
  };

  const handleDeleteAssistant = async ({ deleteLocal, deleteOpenAI }) => {
    if (!selectedAssistant) return;
  
    try {
      await api.request({
        url: `/assistants/${selectedAssistant.idassistant}`,
        method: "DELETE",
        data: {
          deleteLocal,
          deleteOpenAI,
          apiKeyId: selectedAssistant.apiKeyId
        }
      });
  
      if (deleteLocal) {
        dispatch({ type: "DELETE_ASSISTANT", payload: selectedAssistant.id });
        toast.success(i18n.t("assistant.messages.deleted"));
      } else {
        toast.success(i18n.t("assistant.messages.removedFromOpenAI"));
      }
  
    } catch (err) {
      toastError(err);
    }
  
    setSelectedAssistant(null);
    setDeleteDialogOpen(false);
  };
  
  

  const handleOpenAssistantModal = () => {
    setSelectedAssistant(null);
    setAssistantModalOpen(true);
  };

  const handleEditAssistant = (assistant) => {
    setSelectedAssistant(assistant);
    setAssistantModalOpen(true);
  };

  const handleSyncWithOpenAI = async (apiKeyId) => {
    setLoadingSync(true);
    try {
      await api.get("/prompt-assistants", {
        headers: { "x-apikey-id": apiKeyId }
      });
      await getAssistants();
      toast.success(i18n.t("assistant.messages.fetchedFromOpenAI"));
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingSync(false);
      setKeyModalOpen(false);
    }
  };  

  return (
    <MainContainer>
      <DeleteAssistantConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteAssistant}
      />

      <AssistantModal
        open={assistantModalOpen}
        onClose={() => setAssistantModalOpen(false)}
        assistantId={selectedAssistant?.idassistant}
        refreshAssistants={getAssistants}
      />

      <ApiKeySelectorModal
        open={keyModalOpen}
        onClose={() => setKeyModalOpen(false)}
        onSelect={handleSyncWithOpenAI}
      />

      <MainHeader>
        <Title>{i18n.t("assistant.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenAssistantModal}
          >
            {i18n.t("assistant.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => setKeyModalOpen(true)}
          disabled={loadingSync}
        >
          {i18n.t("assistant.buttons.fetchFromOpenAI")}
        </Button>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="left">{i18n.t("assistant.table.idassistant")}</TableCell>
              <TableCell align="left">{i18n.t("assistant.table.name")}</TableCell>
              <TableCell align="left">{i18n.t("assistant.table.model")}</TableCell>
              <TableCell align="center">{i18n.t("assistant.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {assistants.map((assistant) => (
                <TableRow key={assistant.idassistant}>
                  <TableCell align="left">{assistant.idassistant}</TableCell>
                  <TableCell align="left">{assistant.name}</TableCell>
                  <TableCell align="left">{assistant.model}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleEditAssistant(assistant)}>
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedAssistant(assistant);
                        //setConfirmModalOpen(true);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <DeleteOutline />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={4} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Assistants;
